import { DeleteOutlined, DoneAllOutlined, NotificationsOutlined } from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { deleteNotification, updateNotification } from '../../graphql/mutations';
import { notificationsByTenant } from '../../graphql/queries';
import { onNotification } from '../../graphql/subscriptions';
import { PageAppBar } from '../../components/pageAppBar';
import { cardStyle } from '../../theme';

export function Notifications() {
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [subjectFilter, setSubjectFilter] = useState('');
    const [readFilter, setReadFilter] = useState('all'); // 'all' | 'unread' | 'read'

    useEffect(() => {
        if (!userContext.tenantId || !userContext.id) return;

        let subscription;

        async function loadNotifications() {
            try {
                setLoading(true);
                const result = await client.graphql({
                    query: notificationsByTenant,
                    variables: {
                        tenant: userContext.tenantId,
                        cognitoId: { eq: userContext.id },
                        sortDirection: 'DESC',
                        limit: 200,
                    },
                });
                const items = result.data.notificationsByTenant.items || [];
                items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setNotifications(items);
            } catch (err) {
                console.error('Error loading notifications:', err);
            }
            setLoading(false);
        }

        loadNotifications();

        subscription = client.graphql({
            query: onNotification,
            variables: { tenant: userContext.tenantId },
        }).subscribe({
            next: ({ data }) => {
                const newNotification = data.onNotification;
                if (newNotification && newNotification.cognitoId === userContext.id) {
                    setNotifications((prev) => [newNotification, ...prev]);
                }
            },
            error: (err) => console.error('Notification subscription error:', err),
        });

        return () => {
            if (subscription) subscription.unsubscribe();
        };
    }, [userContext.tenantId, userContext.id]);

    const handleNotificationClick = async (notification) => {
        setSelectedNotification(notification);
        if (!notification.read) {
            try {
                await client.graphql({
                    query: updateNotification,
                    variables: { input: { id: notification.id, read: true } },
                });
                setNotifications((prev) =>
                    prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
                );
            } catch (err) {
                console.error('Error marking notification as read:', err);
            }
        }
    };

    const handleDeleteNotification = async (event, notification) => {
        event.stopPropagation();
        try {
            await client.graphql({
                query: deleteNotification,
                variables: { input: { id: notification.id } },
            });
            setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
            if (selectedNotification?.id === notification.id) {
                setSelectedNotification(null);
            }
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    const handleMarkAllRead = async () => {
        const unread = notifications.filter((n) => !n.read);
        try {
            await Promise.all(
                unread.map((n) =>
                    client.graphql({
                        query: updateNotification,
                        variables: { input: { id: n.id, read: true } },
                    })
                )
            );
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch (err) {
            console.error('Error marking all as read:', err);
        }
    };

    const renderNotificationBody = (notification) => {
        const { body, link } = notification;
        if (!body) return null;

        const regex = /\{\{(.+?)\}\}/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(body)) !== null) {
            if (match.index > lastIndex) {
                parts.push(body.slice(lastIndex, match.index));
            }
            const linkText = match[1].trim();
            parts.push(
                <a key={match.index} href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                    {linkText}
                </a>
            );
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < body.length) {
            parts.push(body.slice(lastIndex));
        }

        return parts.length > 0 ? parts : body;
    };

    const filteredNotifications = useMemo(() => {
        return notifications.filter((n) => {
            if (readFilter === 'unread' && n.read) return false;
            if (readFilter === 'read' && !n.read) return false;
            if (subjectFilter && !n.subject?.toLowerCase().includes(subjectFilter.toLowerCase())) return false;
            return true;
        });
    }, [notifications, readFilter, subjectFilter]);

    const unreadCount = notifications.filter((n) => !n.read).length;

    return (
        <Box>
            <PageAppBar title="Notifications" />
            <Box display="grid" gridTemplateColumns="1fr 2fr" gap={3} sx={{ minHeight: 'calc(100vh - 150px)' }}>
                {/* Left panel: notification list */}
                <Card variant="outlined" sx={cardStyle}>
                    <CardHeader
                        title={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h6">Notifications</Typography>
                                {unreadCount > 0 && (
                                    <Chip label={`${unreadCount} unread`} color="primary" size="small" />
                                )}
                            </Box>
                        }
                        action={
                            unreadCount > 0 && (
                                <Tooltip title="Mark all as read">
                                    <IconButton onClick={handleMarkAllRead}>
                                        <DoneAllOutlined color="primary" />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    />
                    <CardContent sx={{ pt: 0 }}>
                        {/* Filters */}
                        <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Filter by subject..."
                                value={subjectFilter}
                                onChange={(e) => setSubjectFilter(e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={readFilter}
                                onChange={(_, value) => { if (value) setReadFilter(value); }}
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="unread">Unread</ToggleButton>
                                <ToggleButton value="read">Read</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {loading && <Typography variant="body2" color="text.secondary">Loading...</Typography>}
                        {!loading && filteredNotifications.length === 0 && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <NotificationsOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">
                                    {notifications.length === 0 ? 'No notifications yet' : 'No notifications match your filters'}
                                </Typography>
                            </Box>
                        )}
                        {!loading && filteredNotifications.length > 0 && (
                            <List disablePadding sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 310px)' }}>
                                {filteredNotifications.map((notification) => (
                                    <ListItemButton
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        selected={selectedNotification?.id === notification.id}
                                        sx={{
                                            backgroundColor: notification.read ? 'transparent' : 'action.hover',
                                            borderRadius: 1,
                                            mb: 0.5,
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <NotificationsOutlined
                                                fontSize="small"
                                                color={notification.read ? 'disabled' : 'primary'}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={notification.subject}
                                            secondary={new Date(notification.createdAt).toLocaleString()}
                                            primaryTypographyProps={{
                                                noWrap: true,
                                                fontWeight: notification.read ? 400 : 600,
                                            }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleDeleteNotification(e, notification)}
                                            sx={{ ml: 1 }}
                                            aria-label="Delete notification"
                                        >
                                            <DeleteOutlined fontSize="small" />
                                        </IconButton>
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </CardContent>
                </Card>

                {/* Right panel: notification detail */}
                <Card variant="outlined" sx={cardStyle}>
                    {selectedNotification ? (
                        <>
                            <CardHeader
                                title={selectedNotification.subject}
                                subheader={new Date(selectedNotification.createdAt).toLocaleString()}
                            />
                            <CardContent>
                                <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {renderNotificationBody(selectedNotification)}
                                </Typography>
                            </CardContent>
                        </>
                    ) : (
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Box textAlign="center">
                                <NotificationsOutlined sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">
                                    Select a notification to view details
                                </Typography>
                            </Box>
                        </CardContent>
                    )}
                </Card>
            </Box>
        </Box>
    );
}
